import React, { useState, useEffect } from 'react';
import { Box, TextField, FormControlLabel, Checkbox, Typography } from '@mui/material';
import CartItems from './CartItems';
import { fetchCartItems, updateCartItemQuantity, removeCartItem, submitOrder } from './api';
import DeliveryForm from './DeliveryForm';
import PickupForm from './PickupForm';
import CustomerInfo from './CustomerInfo';
import Buttons from './Buttons';
import { textFieldStyles } from './formStyles';
import { useFormContext } from './FormContext';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../Cart/CartContext';
import ModalContent from '../static/ModalContent';
import PrivacyPolicyPage from '../static/PrivacyPolicyPage';
import ErrorNotice from '../common/ErrorNotice';

const GENERIC_ERROR_MESSAGE = 'Что-то пошло не так. Попробуйте ещё раз или обновите страницу.';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [receivingType, setReceivingType] = useState('');
  const [cardText, setCardText] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    surname: '',
    phone: '+7'
  });
  const [, setReceivingInfo] = useState({});
  const [activeButton, setActiveButton] = useState('');
  const navigate = useNavigate();
  const { fetchCartCount } = useCart();
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const [privacyPolicyError, setPrivacyPolicyError] = useState(false);
  const [cartError, setCartError] = useState('');
  const [orderError, setOrderError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadCartItems = async () => {
    try {
      await fetchCartItems(setCartItems);
      setCartError('');
    } catch (error) {
      setCartError('Не удалось загрузить корзину. Попробуйте обновить страницу.');
    }
  };

  useEffect(() => {
    loadCartItems();
  }, []);

  const handleReceivingTypeChange = (type) => {
    setReceivingType(type);
    setActiveButton(type);
  };

  const handleQuantityChange = async (itemId, newQuantity) => {
    try {
      if (newQuantity <= 0) {
        await removeCartItem(itemId, loadCartItems);
      } else {
        await updateCartItemQuantity(itemId, newQuantity, loadCartItems);
      }
      setCartError('');
      fetchCartCount();
    } catch (error) {
      setCartError('Не удалось обновить корзину. Попробуйте ещё раз.');
    }
  };

  const { validateForms } = useFormContext();

  // очищаем пустые строки
  const filterEmptyFields = (data) => {
    if (Array.isArray(data)) {
      return data.map(filterEmptyFields);
    } else if (data !== null && typeof data === 'object') {
      return Object.fromEntries(
        Object.entries(data)
          .filter(([_, value]) => value !== '')
          .map(([key, value]) => [key, filterEmptyFields(value)])
      );
    } else {
      return data;
    }
  };

  const handleSubmitOrder = async () => {
    setOrderError('');

    if (!isPrivacyPolicyAccepted) {
      setPrivacyPolicyError(true);
      return;
    }

    // data приходит прямо из результата валидации форм, а не из React state —
    // так заказ не собирается из устаревшего receivingInfo.
    const { isValid, data } = await validateForms();

    if (!isValid) {
      setOrderError('Проверьте, что все обязательные поля заполнены корректно.');
      return;
    }

    const orderData = {
      customer: {
        name: customerInfo.name,
        surname: customerInfo.surname,
        phone: customerInfo.phone
      },
      receiving_type: receivingType,
      receiving_info: filterEmptyFields(data),
      card: cardText,
    };

    setSubmitting(true);
    try {
      const response = await submitOrder(orderData);
      if (response && response.status === 201) {
        navigate('/thank-you');
        return;
      }
      setOrderError(GENERIC_ERROR_MESSAGE);
    } catch (error) {
      const serverError = error?.response?.data?.error;
      setOrderError(
        typeof serverError === 'string'
          ? serverError
          : GENERIC_ERROR_MESSAGE
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Состояние для модального окна
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box sx={{ width: '100%', color: '#000', '& > .MuiBox-root > .MuiBox-root': { p: 1, borderRadius: 2, fontSize: '0.875rem', fontWeight: '700' }}}>
      <Box sx={{ m: 2 }}>
        <Typography variant="h3" gutterBottom>
          Ваш заказ:
          </Typography>
        </Box>
        <Box sx={{ mx: '3%' }}>
          <ErrorNotice>{cartError}</ErrorNotice>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 4, gridTemplateRows: 'auto', gridTemplateAreas: `"cart" "buttons" "customer" "receiving" "order"`, marginLeft: '3%', marginRight: '3%' }}>
          <Box sx={{ gridArea: 'cart', backgroundColor: '#ffeaed', width: '100%' }}>
            <CartItems cartItems={cartItems} onQuantityChange={handleQuantityChange} />
          </Box>

          <Box sx={{ gridArea: 'buttons', backgroundColor: '#fff', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Buttons onClick={() => handleReceivingTypeChange('delivery')} active={activeButton === 'delivery'}>Доставка</Buttons>
            <Buttons onClick={() => handleReceivingTypeChange('pickup')} active={activeButton === 'pickup'}>Самовывоз</Buttons>
          </Box>

          {receivingType && (
            <>
              <Box sx={{ gridArea: 'customer', backgroundColor: '#ffeaed', width: '100%' }}>
                <CustomerInfo setCustomerInfo={setCustomerInfo} />
              </Box>

              <Box sx={{ gridArea: 'receiving', backgroundColor: '#ffeaed', width: '100%' }}>
                {receivingType === 'delivery' ?
                  <DeliveryForm customerInfo={customerInfo} setReceivingInfo={setReceivingInfo} /> :
                  <PickupForm setReceivingInfo={setReceivingInfo} />}
              </Box>

              <Box sx={{ gridArea: 'order', backgroundColor: '#ffeaed', width: '100%' }}>
                <TextField label="Текст открытки" multiline rows={2} value={cardText} onChange={(e) => setCardText(e.target.value)} inputProps={{ maxLength: 119 }} sx={{ ...textFieldStyles, minWidth: 300 }} fullWidth />
                <Box sx={{ display: 'flex' }}>
                <Box sx={{ mt: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isPrivacyPolicyAccepted}
                          onChange={(e) => {
                            setIsPrivacyPolicyAccepted(e.target.checked);
                            setPrivacyPolicyError(false);
                          }}
                        />
                      }
                      label={
                        <span onClick={handleOpenModal} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                          Я согласен с политикой конфиденциальности и даю согласие на обработку моих персональных данных.
                        </span>
                      }
                    />
                    {privacyPolicyError && (
                      <Typography variant="body2" color="error">
                        Пожалуйста, примите политику конфиденциальности, чтобы продолжить.
                      </Typography>
                    )}

                    <ErrorNotice>{orderError}</ErrorNotice>

                    <Box sx={{ mt: 2 }}>
                          <Buttons onClick={handleSubmitOrder} disabled={submitting}>
                            {submitting ? 'Оформляем...' : 'Оформить заказ'}
                          </Buttons>
                    </Box>
                  </Box>


                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ModalContent
      open={modalOpen}
      handleClose={handleCloseModal}

    >
      <PrivacyPolicyPage />
    </ModalContent>
    </>
  );
};

export default CartPage;
